import { addMonths, setDate } from 'date-fns';
import { getFixedT } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Frontend/UI/Button';
import { useLocale } from '../../../Shared/locale';
import { Locale, User } from '../../../Shared/types';
import { HTML } from '../../../Shared/UI/HTML';
import { Charity, CustomMailType, DonationForm, DonationFormMailType, ProjectMailType } from '../../types';

interface Project {
  title: string;
  visible: boolean;
  locale: Locale;
  mail_header_url: string | null;
}

export interface MailTemplateProps {
  type: ProjectMailType | CustomMailType | DonationFormMailType;
  charity: Charity;
  project?: Project;
  donationForm?: DonationForm;
  headerUrl?: string | null;
  body: string;
  user: User;
}

export const createPreviewProject = (charity: Charity) => ({
  title: charity.title,
  visible: true,
  locale: charity.locale,
  mail_header_url: charity.mail_header_url,
});

export default function MailTemplate({
  type,
  charity,
  project,
  donationForm,
  headerUrl,
  body,
  user,
}: MailTemplateProps) {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  const headerSrc = typeof headerUrl !== 'undefined' ? headerUrl : project?.mail_header_url;
  const logoSrc = charity.logo_url;

  const showCampaignSection = project && [
    'new_campaign',
    'donation_received',
    'reminder',
    'target_reached',
    'upcoming_event',
    'upcoming_event_reminder',
    'custom_mail',
  ].includes(type);

  const showCampaignDonorSection = project && type === 'donation_received';
  const showCampaignDonationSection = project && type === 'donation_confirmation';
  const showCampaignDonationMessageSection = project && type === 'donation_confirmation';

  const showOneoffDonationSection = donationForm && type === 'donation_confirmation';
  const showSubscriptionSection = donationForm && [
    'subscription_confirmation',
    'subscription_update',
    'subscription_cancellation',
  ];

  const showUnsubscribe = [
    'donation_received',
    'reminder',
    'target_reached',
    'upcoming_event',
    'upcoming_event_reminder',
    'custom_mail',
  ].includes(type);

  const variableReplacements = {
    first_name: user.first_name,
    last_name: user.last_name,
    full_name: user.full_name,
    ...project && {
      project: project.title,
      funds_raised: formatCurrency(25000),
      url: 'https://supporta.com',
    },
  };

  return (
    <div className="p-4 border rounded border-slate-250 bg-slate-100">
      {headerSrc && (
        <img
          src={headerSrc}
          alt={project?.title || charity.title}
          className="w-[570px] mx-auto rounded-[5px] block mb-4"
        />
      )}
      <div className="max-w-[570px] mx-auto space-y-4 bg-white p-8 rounded-[5px] shadow-sm">
        <div className="space-y-6 [&>*:not(:first-child)]:pt-6 divide-y">
          <div>
            <HTML value={body} replacements={variableReplacements} />
          </div>
          {showCampaignDonorSection && (
            <CampaignDonorSection
              donor={user.first_name}
              project={project}
            />
          )}
          {showCampaignSection && (
            <CampaignSection
              campaigner={user.first_name}
              amount={25000}
              target={25000}
              project={project}
            />
          )}
          {showCampaignDonationMessageSection && (
            <CampaignDonationMessageSection
              project={project}
            />
          )}
          {showCampaignDonationSection && (
            <CampaignDonationSection
              donor={user.first_name}
              project={project}
              charity={charity}
            />
          )}
          {showOneoffDonationSection && (
            <OneoffDonationSection
              charity={charity}
            />
          )}
          {showSubscriptionSection && (
            <SubscriptionSection
              showEndDate={type === 'subscription_cancellation'}
              charity={charity}
            />
          )}
        </div>
      </div>
      <div className="max-w-[570px] mx-auto space-y-6 mt-8">
        {logoSrc && (
          <img
            src={logoSrc}
            alt={charity.title}
            className="w-[80px] mx-auto block"
          />
        )}
        <div className="text-xs text-center text-slate-500">
          <div className="font-bold">
            {charity.title}
          </div>
          {charity.description}
        </div>
        {showUnsubscribe && (
          <div className="text-xs text-center underline text-slate-500">
            {t('backend:manage_email_preferences')}
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignSection({
  campaigner,
  amount,
  target = 0,
  project,
}: {
  campaigner: string;
  amount: number;
  target?: number;
  project: Project;
}) {
  const t = getFixedT(project.locale);
  const { formatCurrency } = useLocale();

  return (
    <div>
      <div className="mb-4 font-semibold text-slate-500">
        {t('backend:your_campaign')}
      </div>
      <div className="p-6 rounded-lg bg-slate-100">
        <div className="mb-2 text-xl font-bold">
          {campaigner}
        </div>
        <div>
          {t(target ? 'backend:funds_raised_of_target' : 'backend:funds_raised', {
            amount: formatCurrency(amount), target: formatCurrency(target),
          })}
        </div>
        <div className="mb-4">
          {project.title}
        </div>
        <div>
          <Button
            variant="project"
            className="pointer-events-none"
          >
            {t('backend:view_your_campaign')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CampaignDonationSection({
  donor,
  project,
  charity,
}: {
  donor: string;
  project: Project;
  charity: Charity;
}) {
  const t = getFixedT(project.locale);
  const { formatCurrency, formatDate } = useLocale();

  return (
    <div>
      <div className="mb-4 font-semibold text-slate-500">
        {t('backend:your_donation')}
      </div>
      <div className="p-6 rounded-lg bg-slate-100">
        <div>
          <strong>
            {t('backend:donation_amount')}:
          </strong>
          {' '}
          {formatCurrency(2500)}
        </div>
        <div>
          <strong>
            {t('backend:donation_date')}:
          </strong>
          {' '}
          {formatDate((new Date).toISOString())}
        </div>
        <div>
          <strong>
            {t('backend:your_name')}:
          </strong>
          {' '}
          {donor}
        </div>
        <hr className="my-4" />
        <div>
          <strong>
            {t('backend:campaign')}:
          </strong>
          {' '}
          <a href="" className="underline pointer-events-none">
            {t('backend:campaign_name_example', { charity: charity.title })}
          </a>
        </div>
        <div>
          <strong>
            {t('backend:project')}:
          </strong>
          {' '}
          {project.visible && (
            <a href="" className="underline pointer-events-none">
              {project.title}
            </a>
          )}
          {!project.visible && project.title}
        </div>
        <div>
          <strong>
            {t('backend:charity')}:
          </strong>
          {' '}
          <a href="" className="underline pointer-events-none">
            {charity.title}
          </a>
        </div>
        <hr className="my-4" />
        <div className="font-bold">
          {t('backend:message')}:
        </div>
        {t('backend:donation_message_example')}
      </div>
    </div>
  );
}

function CampaignDonorSection({
  donor,
  project,
}: {
  donor: string;
  project: Project;
}) {
  const t = getFixedT(project.locale);
  const { formatCurrency } = useLocale();

  return (
    <div>
      <div className="mb-4 font-semibold text-slate-500">
        {t('backend:latest_donation')}
      </div>
      <div className="p-6 space-y-2 rounded-lg bg-slate-100">
        <div className="text-xl font-bold ">
          {formatCurrency(2500)}
        </div>
        <div>
          {t('backend:donation_message_example')}
        </div>
        <div className="font-bold">
          {donor}
        </div>
      </div>
    </div>
  );
}

function CampaignDonationMessageSection({
  project,
}: {
  project: Project;
}) {
  const t = getFixedT(project.locale);

  return (
    <div>
      <div className="mb-4 font-semibold text-slate-500">
        {t('backend:message_from_fundraiser')}
      </div>
      <div className="p-6 rounded-lg bg-slate-100">
        {t('backend:donation_success_message_example')}
      </div>
    </div>
  );
}

function OneoffDonationSection({
  charity,
}: {
  charity: Charity;
}) {
  const t = getFixedT(charity.locale);
  const { formatCurrency, formatDate } = useLocale();

  return (
    <div>
      <div className="mb-4 font-semibold text-slate-500">
        {t('backend:your_donation')}
      </div>
      <div className="p-6 rounded-lg bg-slate-100">
        <div>
          <strong>
            {t('backend:donation_amount')}:
          </strong>
          {' '}
          {formatCurrency(2500)}
        </div>
        <div>
          <strong>
            {t('backend:donation_date')}:
          </strong>
          {' '}
          {formatDate((new Date).toISOString())}
        </div>
        <div>
          <strong>
            {t('backend:charity')}:
          </strong>
          {' '}
          <a href="" className="underline pointer-events-none">
            {charity.title}
          </a>
        </div>
      </div>
    </div>
  );
}

function SubscriptionSection({
  showEndDate,
  charity,
}: {
  showEndDate: boolean;
  charity: Charity;
}) {
  const t = getFixedT(charity.locale);
  const { formatCurrency, formatDate } = useLocale();

  const now = setDate(new Date(), 25);
  const collectDate = new Date() >= now ? addMonths(now, 1) : now;

  return (
    <div>
      <div className="mb-4 font-semibold text-slate-500">
        {t('backend:your_donation')}
      </div>
      <div className="p-6 rounded-lg bg-slate-100">
        <div>
          <strong>
            {t('backend:donation_amount')}:
          </strong>
          {' '}
          {formatCurrency(2500)}
        </div>
        <div>
          <strong>
            {t('backend:frequency')}:
          </strong>
          {' '}
          {t('backend:monthly')}
        </div>
        <div>
          <strong>
            {t('backend:iban')}:
          </strong>
          {' '}
          XXXXXXXXXXXXXX1234
        </div>
        <div>
          <strong>
            {t('backend:charity')}:
          </strong>
          {' '}
          <a href="" className="underline pointer-events-none">
            {charity.title}
          </a>
        </div>
        <div>
          <strong>
            {t('backend:creditor_id')}:
          </strong>
          {' '}
          NL08ZZZ502057730000
        </div>
        <div>
          <strong>
            {t('backend:mandate_reference')}:
          </strong>
          {' '}
          {charity.id.toUpperCase()}123456789
        </div>
        <div>
          <strong>
            {showEndDate ? t('backend:end_date') : t('backend:next_collection')}:
          </strong>
          {' '}
          {formatDate(collectDate.toISOString())}
        </div>
      </div>
    </div>
  );
}
